/**
 * App Context
 * Manages global application state and data caching
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';
import employeeService from '../services/employeeService';
import requestService from '../services/requestService';
import settingsService from '../services/settingsService';
import centerService from '../services/centerService';
import { useAuth } from './AuthContext';

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [requests, setRequests] = useState([]);
  const [settings, setSettings] = useState(null);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load all employees
   */
  const loadEmployees = useCallback(async () => {
    try {
      logger.info('Loading employees', { operation: 'loadEmployees' });
      setLoading(true);
      setError(null);
      
      const data = await employeeService.getAllEmployees();
      setEmployees(data);
      
      logger.info('Employees loaded', { count: data.length, operation: 'loadEmployees' });
      return data;
    } catch (err) {
      logger.error('Error loading employees', err, { operation: 'loadEmployees' });
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load all requests
   */
  const loadRequests = useCallback(async () => {
    try {
      logger.info('Loading requests', { operation: 'loadRequests' });
      setLoading(true);
      setError(null);
      
      const data = await requestService.getAllRequests();
      setRequests(data);
      
      logger.info('Requests loaded', { count: data.length, operation: 'loadRequests' });
      return data;
    } catch (err) {
      logger.error('Error loading requests', err, { operation: 'loadRequests' });
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load settings
   */
  const loadSettings = useCallback(async () => {
    try {
      logger.info('Loading settings', { operation: 'loadSettings' });
      setLoading(true);
      setError(null);
      
      const data = await settingsService.getSettings();
      setSettings(data);
      
      logger.info('Settings loaded', { operation: 'loadSettings' });
      return data;
    } catch (err) {
      logger.error('Error loading settings', err, { operation: 'loadSettings' });
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load all centers
   */
  const loadCenters = useCallback(async () => {
    try {
      logger.info('Loading centers', { operation: 'loadCenters' });
      setLoading(true);
      setError(null);
      
      const data = await centerService.getAllCenters();
      setCenters(data);
      
      logger.info('Centers loaded', { count: data.length, operation: 'loadCenters' });
      return data;
    } catch (err) {
      logger.error('Error loading centers', err, { operation: 'loadCenters' });
      setError(err.message);
      // Don't throw - centers might not be configured yet
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refresh all data
   */
  const refreshAll = useCallback(async () => {
    try {
      logger.info('Refreshing all data', { operation: 'refreshAll' });
      await Promise.all([loadEmployees(), loadRequests(), loadSettings(), loadCenters()]);
    } catch (err) {
      logger.error('Error refreshing data', err, { operation: 'refreshAll' });
      throw err;
    }
  }, [loadEmployees, loadRequests, loadSettings, loadCenters]);

  /**
   * Get user's requests
   */
  const getUserRequests = useCallback(() => {
    if (!user) return [];
    return requests.filter(req => req.employeeId === user.id);
  }, [user, requests]);

  /**
   * Get team requests (for managers)
   */
  const getTeamRequests = useCallback(async () => {
    if (!user || !user.managerId && user.role !== 'manager' && user.role !== 'admin') {
      return [];
    }
    
    if (user.role === 'admin') {
      return requests;
    }
    
    const teamMembers = await employeeService.getEmployeesByManager(user.id);
    const teamMemberIds = teamMembers.map(m => m.id);
    return requests.filter(req => teamMemberIds.includes(req.employeeId));
  }, [user, requests]);

  /**
   * Get pending requests for manager
   */
  const getPendingRequests = useCallback(() => {
    if (!user || (user.role !== 'manager' && user.role !== 'admin')) {
      return [];
    }
    
    if (user.role === 'admin') {
      return requests.filter(req => req.status === 'pending');
    }
    
    return requests.filter(
      req => req.managerId === user.id && req.status === 'pending'
    );
  }, [user, requests]);

  /**
   * Recalculate token usage for all employees based on approved requests
   * This ensures tokensUsed only counts approved requests
   */
  const recalculateAllTokenUsage = useCallback(async () => {
    try {
      logger.info('Recalculating token usage for all employees', { operation: 'recalculateAllTokenUsage' });
      
      const allRequests = await requestService.getAllRequests();
      const allEmployees = await employeeService.getAllEmployees();
      
      // Recalculate for each employee
      await Promise.all(
        allEmployees.map(employee =>
          employeeService.recalculateTokenUsage(employee.id, allRequests)
        )
      );
      
      // Refresh data to reflect updated token usage
      await refreshAll();
      
      logger.info('Token usage recalculated for all employees', { operation: 'recalculateAllTokenUsage' });
    } catch (error) {
      logger.error('Error recalculating token usage', error, { operation: 'recalculateAllTokenUsage' });
    }
  }, [refreshAll]);

  /**
   * Initialize app data
   */
  useEffect(() => {
    if (user) {
      logger.info('Initializing app data', { userId: user.id });
      refreshAll().then(() => {
        // Recalculate token usage to ensure accuracy
        recalculateAllTokenUsage().catch(err => {
          logger.error('Error recalculating token usage', err);
        });
      }).catch(err => {
        logger.error('Error initializing app data', err);
      });
    }
  }, [user, refreshAll, recalculateAllTokenUsage]);

  const value = {
    employees,
    requests,
    settings,
    centers,
    loading,
    error,
    loadEmployees,
    loadRequests,
    loadSettings,
    loadCenters,
    refreshAll,
    recalculateAllTokenUsage,
    getUserRequests,
    getTeamRequests,
    getPendingRequests,
    setEmployees,
    setRequests,
    setSettings,
    setCenters,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

